import { first } from 'remeda';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import {
  CreditAgreement,
  FormulaEngine,
} from '@finley-technologies/finley-types';

import { WipConfig } from 'packages/creditAgreements/ui/types';

const createPrincipalBalanceDailyAverageConfig = (
  wipConfig: WipConfig,
): CreditAgreement.Config.CommonPostgresQuery.Config.PrincipalBalanceDailyAverageQueryConfig => ({
  startDateFormula: {
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
    type: 'formula',
    formula: 'EOMONTH(triggerDate, -2) + 1',
  },
  endDateFormula: {
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
    type: 'formula',
    formula: 'EOMONTH(triggerDate, -1)',
  },
  capitalProviderSlugs: {
    type: 'capitalProviderSlugArray',
    slugs: Object.values(wipConfig.capitalProvidersMap).map((cp) => cp.slug),
  },
});

const createTAccountsSumConfig = (): CreditAgreement.Config.CommonPostgresQuery.Config.TAccountsSumQueryConfig => ({
  startDateFormula: {
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
    type: 'formula',
    formula: 'EOMONTH(triggerDate, -2) + 1',
  },
  endDateFormula: {
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
    type: 'formula',
    formula: 'EOMONTH(triggerDate, -1)',
  },
  tAccountDetails: { type: 'tAccountDetailsArray', accountDetails: [] },
});

const createPrincipalBalancePointInTimeConfig = (): CreditAgreement.Config.CommonPostgresQuery.Config.PrincipalBalancePointInTimeQueryConfig => ({
  dateFormula: {
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
    type: 'formula',
    formula: 'EOMONTH(triggerDate, -1)',
  },
});

const createOrgProvidedInputConfig = (
  wipConfig: WipConfig,
): CreditAgreement.Config.CommonPostgresQuery.Config.OrgProvidedInputQueryConfig => ({
  key: {
    type: 'orgProvidedInputKey',
    key: first(wipConfig.orgProvidedInputKeys) ?? 'NO_KEYS_CONFIGURED',
  },
  reportingDateFormula: {
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
    type: 'formula',
    formula: 'EOMONTH(triggerDate, -1)',
  },
  reportingPeriod: {
    type: 'reportingPeriod',
    reportingPeriod: {
      period: 'calendarMonth',
    },
  },
});

export const updatePostgresQueryShell = ({
  query,
  wipConfig,
}: {
  query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query;
  wipConfig: WipConfig;
}): CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query => {
  const { type } = query;
  switch (type) {
    case 'principalBalanceDailyAverage':
      return {
        ...query,
        parameterConfig: createPrincipalBalanceDailyAverageConfig(wipConfig),
      };
    case 'principalBalancePointInTime':
      return {
        ...query,
        parameterConfig: createPrincipalBalancePointInTimeConfig(),
      };
    case 'tAccountsSum':
      return { ...query, parameterConfig: createTAccountsSumConfig() };
    case 'orgProvidedInput': {
      return {
        ...query,
        parameterConfig: createOrgProvidedInputConfig(wipConfig),
      };
    }
    default:
      return finleyAssertUnreachable(type);
  }
};
